import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Textarea from '../../../../components/common/Textarea';
import { translate } from '../../../../utils/Common';
import styles from './Exceptions.module.css';

/** Mirrors EXCEPTION_CODE_REGEX in api-boxit-admin-usa (settings.utils.js). */
const CODE_REGEX = /^[A-Z0-9_]+$/;

const EMPTY = {
  code: '',
  description: '',
  engName: '',
  espName: '',
  porName: '',
  customerFacing: false,
};

const MAX = { code: 50, description: 200, name: 500 };

/**
 * Create / edit form for a ConfExceptions row. Validated against the same rules the API enforces so
 * a bad payload never reaches it.
 */
const ExceptionFormModal = ({ isOpen, exception, saving, onClose, onSubmit }) => {
  const isEdit = Boolean(exception?.exceptionId);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setForm(
      exception
        ? {
            code: exception.code ?? '',
            description: exception.description ?? '',
            engName: exception.engName ?? '',
            espName: exception.espName ?? '',
            porName: exception.porName ?? '',
            customerFacing: Boolean(exception.customerFacing),
          }
        : EMPTY
    );
  }, [isOpen, exception]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
  };

  const validate = () => {
    const next = {};
    const code = form.code.trim();

    if (!code) next.code = translate('dashboard.configuration.exceptions.errors.codeRequired');
    else if (code.length > MAX.code) next.code = translate('dashboard.configuration.exceptions.errors.codeTooLong');
    else if (!CODE_REGEX.test(code)) next.code = translate('dashboard.configuration.exceptions.errors.codeFormat');

    if (!form.description.trim()) next.description = translate('dashboard.configuration.exceptions.errors.descriptionRequired');
    else if (form.description.trim().length > MAX.description)
      next.description = translate('dashboard.configuration.exceptions.errors.descriptionTooLong');

    ['engName', 'espName', 'porName'].forEach((field) => {
      const value = form[field].trim();
      if (!value) next[field] = translate('dashboard.configuration.exceptions.errors.nameRequired');
      else if (value.length > MAX.name) next[field] = translate('dashboard.configuration.exceptions.errors.nameTooLong');
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /** Escape / backdrop must not discard the form mid-save. */
  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving || !validate()) return;

    onSubmit({
      code: form.code.trim(),
      description: form.description.trim(),
      engName: form.engName.trim(),
      espName: form.espName.trim(),
      porName: form.porName.trim(),
      customerFacing: Boolean(form.customerFacing),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick={!saving}
      showCloseButton={!saving}
      size="lg"
      title={
        isEdit
          ? translate('dashboard.configuration.exceptions.editTitle')
          : translate('dashboard.configuration.exceptions.newTitle')
      }
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <p className={styles.formHelp}>{translate('dashboard.configuration.exceptions.formHelp')}</p>

        <div className={styles.formGrid}>
          <Input
            label={translate('dashboard.configuration.exceptions.code')}
            value={form.code}
            onChange={(e) => setField('code', e.target.value.toUpperCase())}
            placeholder="DAMAGED_PACKAGE"
            helperText={translate('dashboard.configuration.exceptions.codeHelp')}
            error={errors.code}
            maxLength={MAX.code}
            required
            fullWidth
            disabled={saving}
          />

          <Input
            label={translate('dashboard.configuration.exceptions.engName')}
            value={form.engName}
            onChange={(e) => setField('engName', e.target.value)}
            error={errors.engName}
            maxLength={MAX.name}
            required
            fullWidth
            disabled={saving}
          />

          <Input
            label={translate('dashboard.configuration.exceptions.espName')}
            value={form.espName}
            onChange={(e) => setField('espName', e.target.value)}
            error={errors.espName}
            maxLength={MAX.name}
            required
            fullWidth
            disabled={saving}
          />

          <Input
            label={translate('dashboard.configuration.exceptions.porName')}
            value={form.porName}
            onChange={(e) => setField('porName', e.target.value)}
            error={errors.porName}
            maxLength={MAX.name}
            required
            fullWidth
            disabled={saving}
          />
        </div>

        <Textarea
          label={translate('global.description')}
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
          helperText={translate('dashboard.configuration.exceptions.descriptionHelp')}
          error={errors.description}
          maxLength={MAX.description}
          rows={3}
          required
          fullWidth
          disabled={saving}
        />

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={form.customerFacing}
            onChange={(e) => setField('customerFacing', e.target.checked)}
            disabled={saving}
          />
          <span>
            <strong>{translate('dashboard.configuration.exceptions.customerFacing')}</strong>
            <small>{translate('dashboard.configuration.exceptions.customerFacingHelp')}</small>
          </span>
        </label>

        <div className={styles.formActions}>
          <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
            {translate('global.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={saving} disabled={saving}>
            {isEdit ? translate('global.save') : translate('global.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExceptionFormModal;
